import api from './api';

/**
 * Service for handling data deduplication operations
 */
const deduplicationService = {
  /**
   * Check for duplicate files based on hash
   * @param {string} fileHash - Hash of the file content
   * @returns {Promise<object>} List of potential duplicates
   */
  checkDuplicates: async (fileHash) => {
    try {
      const response = await api.get(`/files/duplicates/${fileHash}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Calculate file hash before upload
   * @param {File} file - The file object to calculate hash for
   * @returns {Promise<string>} The calculated hash
   */
  calculateFileHash: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target?.result;
          if (!arrayBuffer) {
            throw new Error('Failed to read file');
          }
          
          // Use SubtleCrypto API to calculate SHA-256 hash
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          resolve(hashHex);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  },

  /**
   * Scan for duplicates across the user's storage
   * @param {object} options - Scan options (folders, file types)
   * @returns {Promise<object>} Scan results with duplicates grouped
   */
  scanForDuplicates: async (options = {}) => {
    try {
      const response = await api.post('/files/scan-duplicates', options);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Resolve duplicates by keeping only specified files
   * @param {string[]} keepFileIds - IDs of files to keep
   * @param {string[]} removeFileIds - IDs of files to remove
   * @returns {Promise<object>} Result of the operation
   */
  resolveDuplicates: async (keepFileIds, removeFileIds) => {
    try {
      const response = await api.post('/files/resolve-duplicates', { 
        keepFileIds, 
        removeFileIds 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Get deduplication statistics
   * @returns {Promise<object>} Statistics about duplicates and saved space
   */
  getDeduplicationStats: async () => {
    try {
      const response = await api.get('/storage/deduplication-stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Enable or disable automatic deduplication
   * @param {boolean} enabled - Whether to enable automatic deduplication
   * @returns {Promise<object>} Updated settings
   */
  setAutomaticDeduplication: async (enabled) => {
    try {
      const response = await api.put('/settings/deduplication', { enabled });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default deduplicationService; 